import { useAppDispatch } from '@/hooks/useReduxHooks';
import {
  setFilterKeyword,
  setFilterOrderBy,
  setFilterSeletor,
} from '@/store/modules/refund';
import { comma } from '@/util/format';
import { Button, Icon } from '@successmode-ktp/kui';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

type Props = {
  openedBottomSheet: any;
  onClose: () => void;
};

function AlertBottomSheet({ openedBottomSheet, onClose }: Props) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const refundInfo = useMemo(() => {
    if (openedBottomSheet) {
      return JSON.parse(openedBottomSheet?.message);
    }
    return null;
  }, [openedBottomSheet]);

  const onGoToRefundPage = async () => {
    await navigate('/refund-detail');

    dispatch(setFilterOrderBy('CREATED_DATE'));
    if (refundInfo?.touristName) {
      dispatch(setFilterKeyword(refundInfo.touristName));
    }

    if (refundInfo?.supplyDate) {
      dispatch(
        setFilterSeletor({
          startDate: new Date(refundInfo.supplyDate),
          endDate: new Date(refundInfo.supplyDate),
        }),
      );
    } else {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      dispatch(
        setFilterSeletor({
          startDate,
          endDate: new Date(),
        }),
      );
    }
  };

  const title = useMemo(() => {
    switch (refundInfo?.type) {
      case 'REFUND_CREATE_CONFIRMED':
        return '환급건이 등록되었습니다';
      case 'REFUND_CREATE_REJECTED':
        return '환급건 등록이 실패되었습니다';
      case 'REFUND_MODIFY_CONFIRMED':
        return '환급건이 수정되었습니다';
      case 'REFUND_MODIFY_REJECTED':
        return '환급건 수정이 실패되었습니다';
      case 'PACKAGE_ACCEPTED':
        return '소모품 요청이 접수되었습니다';
      default:
        return '';
    }
  }, [refundInfo]);

  const subTitle = useMemo(() => {
    switch (refundInfo?.type) {
      case 'REFUND_CREATE_CONFIRMED':
        return '등록된 환급건';
      case 'REFUND_CREATE_REJECTED':
        return '요청한 환급건';
      case 'REFUND_MODIFY_CONFIRMED':
        return '수정된 환급건';
      case 'REFUND_MODIFY_REJECTED':
        return '요청한 환급건';
      case 'PACKAGE_ACCEPTED':
        return '신청한 소모품';
      default:
        return '';
    }
  }, [refundInfo]);

  const isRejectedState =
    refundInfo &&
    (refundInfo?.type === 'REFUND_CREATE_REJECTED' ||
      refundInfo?.type === 'REFUND_MODIFY_REJECTED');

  const isShowRefundPageButton =
    refundInfo &&
    (refundInfo?.type === 'REFUND_CREATE_CONFIRMED' ||
      refundInfo?.type === 'REFUND_MODIFY_CONFIRMED' ||
      refundInfo.type === 'REFUND_MODIFY_REJECTED');

  return (
    <>
      <BottomSheet isOpen={!!openedBottomSheet}>
        <Header>
          <Title>{title}</Title>
          <CloseButton onClick={onClose}>
            <Icon.X size={16} fillColor='#A7A7A7' />
          </CloseButton>
        </Header>
        <Body>
          <Label>
            <span>{subTitle}</span>
            {openedBottomSheet?.createdAt && (
              <Time>
                요청 일시:{' '}
                {format(
                  new Date(openedBottomSheet.createdAt),
                  'yyyy.MM.dd HH:mm',
                )}
              </Time>
            )}
          </Label>

          {refundInfo && (
            <>
              <Content>
                {refundInfo?.type === 'PACKAGE_ACCEPTED' ? (
                  <PackageList>
                    {refundInfo?.packageNames.map((packageName: string) => (
                      <Package>{packageName}</Package>
                    ))}
                    {refundInfo?.etc && (
                      <Package>
                        <strong>기타</strong>
                        <span>{refundInfo?.etc}</span>
                      </Package>
                    )}
                  </PackageList>
                ) : (
                  <>
                    <span>국적 : {refundInfo?.nationality}</span>
                    <span>고객명 : {refundInfo?.touristName}</span>
                    <span>
                      시술 금액 : {comma(refundInfo?.productAmount)}원
                    </span>
                    <span>환급액 : {comma(refundInfo?.refundAmount)}원</span>
                  </>
                )}
              </Content>

              {isRejectedState &&
                (refundInfo?.reason || refundInfo?.rejectionReason) && (
                  <RejectReason>
                    <Label>실패 사유</Label>
                    <Content isRejectReason>
                      {refundInfo?.reason || refundInfo?.rejectionReason}
                    </Content>
                  </RejectReason>
                )}
              {isShowRefundPageButton && (
                <Footer>
                  <Button onClick={onGoToRefundPage}>
                    환급 내역으로 이동하기
                  </Button>
                </Footer>
              )}
            </>
          )}
        </Body>
      </BottomSheet>
      {!!openedBottomSheet && <Overlay onClick={onClose} />}
    </>
  );
}

const BottomSheet = styled.aside<{ isOpen: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 320px;
  background-color: #fff;
  border-radius: 20px;
  padding: 20px 18px 32px 18px;
  z-index: 650;
  transform: translateY(100%);
  opacity: 0;
  transition: all 200ms ease-in-out;

  ${({ isOpen }) =>
    isOpen &&
    css`
      opacity: 1;
      transform: translateY(0);
    `}
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 320px;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.25);
  z-index: 600;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 28px;
`;

const Title = styled.strong`
  font-weight: 600;
  font-size: 16px;
  line-height: 150%;
  letter-spacing: -2%;
  color: #3a3b3e;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  width: 30px;
  height: 30px;
  border-radius: 100px;
  background-color: #f5f6f7;
`;

const Body = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 6px;
  font-weight: 500;
  font-size: 14px;
  line-height: 150%;
  letter-spacing: -2%;
  color: #5f6165;
`;

const Time = styled.span`
  font-weight: 400;
  font-size: 12px;
  color: #80848a;
`;

const Content = styled.div<{ isRejectReason?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 12px 20px;
  border-radius: 6px;
  background-color: ${({ isRejectReason = false }) =>
    isRejectReason ? '#ED08280D' : '#f5f6f7'};
  color: ${({ isRejectReason = false }) =>
    isRejectReason ? '#FF2D55E5' : '#5F6165'};
  font-weight: 500;
  font-size: 14px;
  line-height: 150%;
  letter-spacing: -2%;
`;

const Footer = styled.div``;

const PackageList = styled.ul`
  display: inline-flex;
  align-items: flex-start;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 6px;
`;

const Package = styled.li`
  padding: 5px 8px;
  display: inline-flex;
  align-items: flex-start;
  justify-content: center;
  border-radius: 8px;
  background-color: #fff;
  font-weight: 700;
  font-size: 12px;
  line-height: 150%;
  letter-spacing: -2%;
  color: #80848a;

  strong {
    flex-shrink: 0;
  }

  span {
    position: relative;
    font-weight: 400;
    margin-left: 20px;
    &:before {
      content: '';
      position: absolute;
      top: 2px;
      left: -10px;
      width: 1px;
      height: 14px;
      background-color: #a7a7a7;
    }
  }
`;

const RejectReason = styled.div`
  margin-top: 12px;
`;

export default AlertBottomSheet;
